import React, { ReactElement, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { specialCharactersValidation } from "constants/regexp";
import { websiteContainerDatabaseSteps } from "constants/server";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { getDockerDbTypes, getDockerDbVersions } from "redux/handlers/dockerHandler";
import { generatePassword } from "redux/handlers/userHandler";
import { postInstallDatabase } from "redux/handlers/webAppHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { dbData } from "types/global/container";

import DbRecap from "./databaseStepComponents/DbRecap";

const initialValue = {
 name: "",
 type: "",
 version: "",
 user: "",
 password: ""
};

const CreateDatabase = ({ id }: { id: number }): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [goToStepper, setGoToStepper] = useState<boolean>(false);
 const [activeStep, setActiveStep] = useState<number>(0);
 const [dbTypes, setDbTypes] = useState<Array<{ id: number; name: string }>>([]);
 const [dbVersions, setDbVersions] = useState<Array<{ id: number; name: string }>>([]);
 const [databaseData, setDatabaseData] = useState<dbData>(initialValue);
 const [showPassword, setShowPassword] = useState<string>("password");

 const handleGeneratePassword = async () => {
  handleChange("password", await generatePassword());
 };

 const handleOpen = async () => {
  setGoToStepper(false);
  setDbTypes(await dispatch(getDockerDbTypes()));
  handleReset();
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postInstallDatabase(
    id,
    databaseData.name,
    databaseData.user,
    databaseData.password,
    databaseData.type,
    databaseData.version
   )
  );
  setLoading(false);
  handleClose();
 };

 const handleNext = () => {
  setActiveStep((prevActiveStep) => prevActiveStep + 1);
 };

 const handleBack = () => {
  setActiveStep((prevActiveStep) => prevActiveStep - 1);
 };

 const handleReset = () => {
  setDatabaseData(initialValue);
  setActiveStep(0);
 };

 const handleChange = (key: keyof dbData, value: string) => {
  setDatabaseData({ ...databaseData, [key]: value });
 };

 useEffect(() => {
  (async () => {
   if (databaseData.type !== "")
    setDbVersions(await dispatch(getDockerDbVersions(databaseData.type)));
  })();
 }, [databaseData.type]);

 const RenderStepContent = (): ReactElement => {
  switch (activeStep) {
   case 0:
    return (
     <TextField
      autoComplete="off"
      value={databaseData.name}
      autoFocus
      label={intl.formatMessage({ id: "docker.website.databaseName" })}
      InputLabelProps={{ shrink: true }}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
       e.stopPropagation();
      }}
      onChange={({ currentTarget }) => handleChange("name", currentTarget.value)}
     />
    );
   case 1:
    return (
     <FormControl fullWidth>
      <InputLabel>
       <FormattedMessage id="docker.website.databaseType" />
      </InputLabel>
      <Select
       value={databaseData.type}
       label={<FormattedMessage id="docker.website.databaseType" />}
       onChange={(e) => handleChange("type", e.target.value)}>
       {dbTypes.map((element, index) => {
        return (
         <MenuItem value={element.name} key={`php-vers-${index}`}>
          {element.name}
         </MenuItem>
        );
       })}
      </Select>
     </FormControl>
    );
   case 2:
    return (
     <FormControl fullWidth>
      <InputLabel>
       <FormattedMessage id="docker.website.databaseVersion" />
      </InputLabel>
      <Select
       value={databaseData.version}
       label={<FormattedMessage id="docker.website.databaseVersion" />}
       onChange={(e) => handleChange("version", e.target.value)}>
       {dbVersions.map((element, index) => {
        return (
         <MenuItem value={element.name} key={`db-vers-${index}`}>
          {element.name}
         </MenuItem>
        );
       })}
      </Select>
     </FormControl>
    );
   case 3:
    return (
     <TextField
      autoComplete="off"
      value={databaseData.user}
      autoFocus
      label={intl.formatMessage({ id: "docker.website.databaseUser" })}
      InputLabelProps={{ shrink: true }}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
       e.stopPropagation();
      }}
      onChange={({ currentTarget }) => handleChange("user", currentTarget.value)}
     />
    );
   case 4:
    return (
     <Stack direction="row" alignItems="center" spacing={2}>
      <TextField
       value={databaseData.password}
       onChange={({ currentTarget }) => handleChange("password", currentTarget.value)}
       fullWidth={true}
       autoFocus
       type={showPassword}
       autoComplete="new-password"
       label={intl.formatMessage({ id: "docker.website.databasePassword" })}
       error={!specialCharactersValidation.test(databaseData.password)}
       helperText={
        !specialCharactersValidation.test(databaseData.password) && (
         <FormattedMessage id="docker.website.databasePasswordError" />
        )
       }
       InputProps={{
        endAdornment: (
         <InputAdornment position="end">
          <IconButton
           aria-label="toggle password visibility"
           onClick={() => setShowPassword(showPassword === "password" ? "text" : "password")}
           edge="end">
           <IconSelector
            icon={showPassword === "text" ? "VisibilityOffIcon" : "VisibilityIcon"}
            props={{ fontSize: "small" }}
           />
          </IconButton>
         </InputAdornment>
        )
       }}
      />
      <Button onClick={handleGeneratePassword} sx={{ width: "40%" }}>
       <FormattedMessage id="app.generatePassword" />
      </Button>
     </Stack>
    );
   case 5:
    return <DbRecap databaseData={databaseData} />;
   default:
    return <></>;
  }
 };

 const handleConfirmThird = () => {
  if (!goToStepper) {
   setGoToStepper(true);
  } else if (activeStep === 5) {
   handleConfirm();
  } else {
   handleNext();
  }
 };

 const renderThirdText = (): string => {
  if (!goToStepper) {
   return "docker.website.startNow";
  } else if (activeStep === 5) {
   return "app.confirm";
  } else {
   return "app.next";
  }
 };

 const checkDisableThird = (): boolean => {
  return (
   goToStepper &&
   (loading ||
    (activeStep === 0 && (databaseData.name === "" || databaseData.name.includes(" "))) ||
    (activeStep === 1 && databaseData.type === "") ||
    (activeStep === 2 && databaseData.version === "") ||
    (activeStep === 3 && (databaseData.user === "" || databaseData.user.includes(" "))) ||
    (activeStep === 4 &&
     (databaseData.password === "" || !specialCharactersValidation.test(databaseData.password))))
  );
 };

 return (
  <>
   <Button onClick={handleOpen}>
    <FormattedMessage id="docker.website.createDatabase" />
   </Button>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "docker.website.createDatabase" })}
    handleClose={() => (goToStepper ? handleBack() : handleClose())}
    handleConfirm={handleReset}
    thirdOption={true}
    thirdText={renderThirdText()}
    disabled={loading}
    handleConfirmThird={handleConfirmThird}
    confirmText={"app.reset"}
    disableCancel={activeStep === 0 && goToStepper}
    showConfirmButton={goToStepper}
    disabledThird={loading || checkDisableThird()}>
    {!goToStepper ? (
     <Stack>
      <Stack direction="row">
       <Stack justifyContent="center" spacing={2}>
        <Typography>
         <FormattedMessage id="docker.website.firstExplainDatabase" />
        </Typography>
        <Typography>
         <FormattedMessage id="docker.website.secondExplainDatabase" />
        </Typography>
        <Typography>
         <FormattedMessage id="docker.website.thirdExplainDatabase" />
        </Typography>
       </Stack>
       <Stack alignItems="center" justifyContent="center">
        <IconSelector
         icon="DatabaseIcon"
         props={{ sx: { fontSize: "200px", color: "lightgray" } }}
        />
       </Stack>
      </Stack>
     </Stack>
    ) : (
     <Stack>
      <Stepper activeStep={activeStep} alternativeLabel>
       {websiteContainerDatabaseSteps.map((label) => (
        <Step key={label.label}>
         <StepLabel optional={label.description ? <FormattedMessage id={label.description} /> : ""}>
          {<FormattedMessage id={label.label} />}
         </StepLabel>
        </Step>
       ))}
      </Stepper>
      <Stack p={2}>
       <RenderStepContent />
      </Stack>
     </Stack>
    )}
   </AppModal>
  </>
 );
};

export default CreateDatabase;
